import DateUtilsService from "external-services/date-utils-service";
import {IBookingInfoAtTime} from "external-services/booking-availability-service/types";
import moment, {Moment} from "moment";
import {IServiceList, ITimesForDropdown} from "floorplan-v2/app/services/FloorplanHeaderService/types";
import {cloneDeep, sortBy} from "lodash-es";
import {IServiceItem, IServiceListView, ISimpleSelectTimeDropdown, TimeStatus} from "shared-types/SharedTypes";


const NS = 'FloorplanHeaderService';
class FloorplanHeaderService {

  doUpdateTimes(hasTimes: boolean, selectedMoment: Moment, currentServiceTimes: IBookingInfoAtTime[]): boolean {
    if (hasTimes) { // only prevent extra returnTimes calls if times already loaded

      if (!selectedMoment || !currentServiceTimes?.length) {
        return false;
      }

      // don't allow if selectedMoment is a different day/schedule to the first time in currentServiceTimes
      if (!DateUtilsService.isOnSameDate(selectedMoment.toDate(), currentServiceTimes[0].time, true)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Returns the times for the times dropdown - this adds the current time once its set
   */
  getTimesForDropdown(availableTimesVal: IBookingInfoAtTime[], isTimeNow: boolean, timeZoneId: string): ITimesForDropdown[] {
    const times: ITimesForDropdown[] = availableTimesVal.map((time, index) => {
      return {
        name: moment(time.time).format('LT'),
        value: time.time,
        rawData: time,
        indexVal: index,
      };
    });
    if (isTimeNow && times.length) {
      const venueTime = DateUtilsService.getVenueTime(timeZoneId);
      const clearDate = DateUtilsService.zeroOutMinutes(venueTime); // so that its easy to compare the duplicate times later on
      const currentDate = moment(clearDate);
      const newTime = {
        name: `Now ${currentDate.format('LT')}`,
        value: clearDate,
        rawData: { time: venueTime } as unknown as IBookingInfoAtTime,
        indexVal: -1,
      };
      times.unshift(newTime);
    }
    return times;
  }

  getClosestFocussedValue(updatedAvailableTimes: ISimpleSelectTimeDropdown[], selectedFloorplanTime: Date): string {
    if (updatedAvailableTimes.length) {
      // removing the custom time - the 1st item in updatedAvailableTimes
      const dropdownTimes = cloneDeep(updatedAvailableTimes);
      dropdownTimes.shift();
      const closestTimeObj = this.getClosestValue(dropdownTimes, selectedFloorplanTime, -1, selectedFloorplanTime);
      if (closestTimeObj) {
        const focussedValue = updatedAvailableTimes[closestTimeObj.closestTimeIndex]?.value; // focus to 2 above the selected value
        return focussedValue?.toString();
      }
    }
    return null;
  }

  getClosestValue(timeList: ISimpleSelectTimeDropdown[], closestTo: Date, increment: number, selectedFloorplanTime: Date){
    const sortedTimes = sortBy(timeList, 'value');
    const selectedTimeIndexSorted = sortedTimes.findIndex(t => t.value.getTime() === selectedFloorplanTime?.getTime());
    let updatedList: ISimpleSelectTimeDropdown[] = sortedTimes; // if 0 passed for increment, use the sortedTimes as it is
    if (increment === 1) {
      // for forward arrow we only care about the time in the future than the selected time
      updatedList = sortedTimes.slice(selectedTimeIndexSorted + 1, sortedTimes.length);
    } else if (increment === -1) {
      // for back arrow we only care abt times in the past than the selected time
      updatedList = sortedTimes.slice(0, selectedTimeIndexSorted);
    }

    if (updatedList.length && closestTo) {
      const closestTimeInDropdown = DateUtilsService.getClosestDate(updatedList, closestTo);
      const closestTimeIndex = timeList.findIndex(t => t.value.getTime() === closestTimeInDropdown.getTime());
      return {closestTimeIndex, closestTimeInDropdown}
    }
    return null;
  }

  isInSelectedService(isDateToday: boolean, currentServiceViews: IServiceListView[], selectedServiceIndex: number, timeZoneId: string): boolean {
    if (!isDateToday) {
      return false;
    }
    const selectedServiceView = currentServiceViews[selectedServiceIndex];
    if (!selectedServiceView) {
      return true;// should be all service for initial.
    }
    //UI-1246 if selected active service, set to now time
    if (selectedServiceView.service) {
      const venueTime = DateUtilsService.getVenueTime(timeZoneId);
      const _service = selectedServiceView.service as IServiceItem;
      return _service.startTime <= venueTime && _service.endTime >= venueTime;
    } else if (selectedServiceView.services) {
      const venueTime = DateUtilsService.getVenueTime(timeZoneId);
      return selectedServiceView.services.some((service) => {
        return service.startTime <= venueTime && service.endTime >= venueTime;
      });
    }
    return !selectedServiceView.service && !selectedServiceView.services;
  }

  getServicesList(currentServiceViews: IServiceListView[]): IServiceList[] {
    const ALL_SERVICES = 'All';
    const ALL_SERVICES_LABEL = 'All Services';

    const result = currentServiceViews?.map((service, index) => {
      const name = service?.name === ALL_SERVICES ? ALL_SERVICES_LABEL : service?.name;
      return {
        name,
        value: index.toString(),
        rawData: service?.service,
      };
    });

    return result;
  }

  /**
   * Checks if venue time is within list of times and is available.
   * Note: can't use service start and end times because we need to check if the times are available as well.
   */
  isVenueTimeWithinAvailableTimes(times: IBookingInfoAtTime[], timeZoneId: string, selectedServiceView: IServiceListView): boolean {

    // "All Services" will have neither service or service group, so should assume time is includes
    if (!selectedServiceView?.service && !selectedServiceView?.services) {
      return true;
    }

    const serviceEndTime: Date = ((selectedServiceView.service as IServiceItem)?.endTime as Date)
      // if no service exists, gets service group's latest end time
      || (selectedServiceView.services as IServiceItem[]).reduce((selectedEndTime: Date, nextService: IServiceItem) => {
        const nextEndTime = nextService.endTime as Date;
        if (!selectedEndTime || nextEndTime > selectedEndTime) {
          return nextEndTime;
        }
        return selectedEndTime;
      }, null);

    const venueTime = DateUtilsService.getVenueTime(timeZoneId);
    const includesCurrentVenueTime = times.some((t, i) => {
      let nextTime: Date = times[i+1]?.time;
      if (!nextTime) {
        nextTime = serviceEndTime;
      }

      /**
       * @todo: UI-1371 investigate why t.status === TimeStatus.available comes back as unavailable. Something to do
       * with service/slice using "Legacy" version of BookingAvailabilityService and passing incorrect bookingsByTable
       * object. Once this is fixed, use below commented out isSameMin instead, which checks if status is available.
       */
      // const isSameMin = t.status === TimeStatus.available && DateUtilsService.isInBetweenDates(venueTime, t.time, nextTime);

      // checks if time is on or after start time (and before end time)
      const isSameMin = DateUtilsService.isInBetweenDates(venueTime, t.time, nextTime);
      return isSameMin;
    });

    return includesCurrentVenueTime;
  }
}

const instance = new FloorplanHeaderService();
export default instance as FloorplanHeaderService;
